/* istanbul ignore file */
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, ContextStore, ExperimentStore, PageStore, RouteStore } from 'bernie-core';
import { ClientLogger } from 'bernie-client';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { PropertyOffers } from '@shared-ui/lodging-property-offers';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { INCOMPATIBLE_RATEPLAN } from 'stores/multi-item-store-util';
import { MultiItemStore } from 'stores/multi-item-store';

import { adaptToCartPropertyPrimaryKeyInput, adaptToCartPropertySearchContextInput } from 'components/multi-item/cart-adapter';
import { SharedUiWrapperProps, withSharedUiPropertyOffers } from './shared-ui-property-offers';

interface SharedUiPropertyOffersProps extends SharedUiWrapperProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  experiment?: ExperimentStore;
  multiItem?: MultiItemStore;
  page?: PageStore;
  routeStore?: RouteStore;
}

const reserveFormLogger = ClientLogger.getLoggerWithIdentifier('pwa.component.reserveForm');

const LinkWrapper: React.FC<{ href?: string; routeStore?: RouteStore }> = inject('routeStore')(({ href, routeStore, children }) => {
  // Temporary until the correct url is returned from the bff.
  // The href value for the error message is only the queryparam, not the
  // whole path.
  // When fixed, no need for the pathname and search anymore
  // TODO: add bff PR
  if (href.includes('?')) {
    return <Link to={href}>{children}</Link>;
  }

  return <Link to={{ pathname: routeStore?.pathname, search: href }}>{children}</Link>;
});

/**
 * Represents two different shared-ui versions of room and rates depending on the offers data given.
 * e.g. if verticalListings, then renders TnL1A version of property-offers
 * otherwise renders control version
 */
const PropertyOffersWrapper: React.FC<SharedUiPropertyOffersProps> = inject(
  'analytics',
  'context',
  'currentHotel',
  'experiment',
  'multiItem',
  'page',
  'routeStore'
)(
  observer(({ children, inputs, data, analytics, context, currentHotel, experiment, multiItem, page, routeStore }) => {
    const handleMultiItemFailure = formRef => reason => {
      if (multiItem?.itemCount === 0) {
        if (typeof formRef?.current?.submit === 'function') {
          formRef.current.submit();
        }
        reserveFormLogger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'FALLBACK_TO_STANDALONE_BOOKING'));
      }
      // this is treating rare usecase, where user tries to reserve a rateplan that they shouldn't have seen in 1st place
      else if (reason === INCOMPATIBLE_RATEPLAN) {
        currentHotel.fetch(null, context, multiItem, experiment, page).then(() => {
          if (routeStore?.queryParams?.pwaDialogNested) {
            routeStore.goBack();
          }
        });
        reserveFormLogger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'INCOMPATIBLE_RATEPLAN'));
      }
    };

    const handleHotelPlusHotelCallBack = (propertyNaturalKeys, businessModel, noCreditCard, multiItemPriceToken, formRef) =>
      multiItem
        .addPropertyToCart(
          context,
          adaptToCartPropertyPrimaryKeyInput(propertyNaturalKeys),
          adaptToCartPropertySearchContextInput(currentHotel.searchCriteria, currentHotel.hotel?.overview?.regionId),
          businessModel,
          analytics,
          noCreditCard
        )
        .catch(handleMultiItemFailure(formRef));

    const handleSelectProductsFailure = reason => {
      if (!multiItem.errorMessage) {
        multiItem.setGenericError();
      }
      reserveFormLogger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FAILED_TO_ADD_HOTEL_TO_MULTIITEM_SESSION'), reason);
    };

    const handleMultiItemCallBack = (propertyNaturalKeys, businessModel, noCreditCard, multiItemPriceToken) =>
      multiItem
        .selectProducts(
          context,
          multiItem.multiItemContext?.id,
          multiItemPriceToken,
          adaptToCartPropertyPrimaryKeyInput(propertyNaturalKeys),
          null,
          null
        )
        .then(redirectUrl => {
          window.location.href = redirectUrl;
        })
        .catch(handleSelectProductsFailure);

    const isMultiItem = multiItem?.isPackageShoppingPath();

    // TODO This part is temporary to enable shared-ui-property-offers on all pages. This part will be moved to shared-ui as well
    const getMultiItemCallBack = () => {
      if (multiItem?.showMultiPropExperience) {
        return handleHotelPlusHotelCallBack;
      } else if (isMultiItem) {
        return handleMultiItemCallBack;
      }
    };

    if (isMultiItem) {
      return (
        <AbacusExperiment name="MIT_PWA_PDP_Select_Products_Mutation_Callback_SharedUI">
          <AbacusControl>
            <PropertyOffers
              inputs={inputs}
              data={data}
              handleMultiItemCallback={getMultiItemCallBack()}
              defaultErrorMessage={multiItem.genericError}
              LinkWrapper={LinkWrapper}
              loading={currentHotel?.loadingOffers}
            >
              {children}
            </PropertyOffers>
          </AbacusControl>
          <AbacusVariant bucket={1}>
            <PropertyOffers
              inputs={inputs}
              data={data}
              defaultErrorMessage={multiItem.genericError}
              LinkWrapper={LinkWrapper}
              loading={currentHotel?.loadingOffers}
            >
              {children}
            </PropertyOffers>
          </AbacusVariant>
        </AbacusExperiment>
      );
    }

    return (
      <PropertyOffers
        inputs={inputs}
        data={data}
        handleMultiItemCallback={getMultiItemCallBack()}
        defaultErrorMessage={multiItem.genericError}
        LinkWrapper={LinkWrapper}
        loading={currentHotel?.loadingOffers}
      >
        {children}
      </PropertyOffers>
    );
  })
);
PropertyOffersWrapper.displayName = 'SharedUiPropertyOffers';
export const SharedUiOffersWrapper = withSharedUiPropertyOffers(PropertyOffersWrapper);
